import { onScopeDispose, shallowRef } from 'vue';
import type { ShallowRef } from 'vue';
import { useUi } from '@/composition/use-ui';
import {
  getServerErrorCode,
  getServerErrorStatus,
} from '@/domain/reservations/reservation-errors';
import { menuImportErrorKey } from '@/domain/store-setup/setup-errors';
import { AlertType } from '@/interfaces/IAlert';
import { ImageUnreadableError } from '@/lib/menu-capture/image-processor';
import { uploadMenuPhoto } from '@/composition/menu-photo-upload';
import { i18n } from '@/plugins/i18n';
import type { IMenuImportSession } from './menu-import-context';

export { DISH_PHOTO_UPLOAD_TIMEOUT_MS } from '@/composition/menu-photo-upload';

export interface IUseDishPhotos {
  /** Dishes whose new photo is being prepared, uploaded or saved. */
  uploading: Readonly<ShallowRef<ReadonlySet<string>>>;
  /**
   * The picked image of a dish: resized in the browser (≤1600 px, JPEG),
   * uploaded, then saved on the dish through the session's queue. It
   * belongs to the menu flow, not to the editor that asked for it, so the
   * photo is saved even when the editor closes meanwhile. False when it
   * failed (the owner was told) or the dish is already getting a photo.
   */
  upload(itemId: string, file: File): Promise<boolean>;
}

/** The copy of a failed dish photo. */
export function dishPhotoErrorKey(error: unknown): string {
  if (error instanceof ImageUnreadableError)
    return 'menuImport.errors.unsupported';
  return (
    menuImportErrorKey(
      getServerErrorCode(error),
      getServerErrorStatus(error)
    ) ?? 'menuImport.errors.uploadFailed'
  );
}

/** Dish photos of the menu workspace (plan §4.5.1 «Foto»). */
export function useDishPhotos(session: IMenuImportSession): IUseDishPhotos {
  const ui = useUi();
  const t = (key: string) => String(i18n.t(key));
  const uploading = shallowRef<ReadonlySet<string>>(new Set());
  let disposed = false;

  function mark(itemId: string, busy: boolean): void {
    const next = new Set(uploading.value);
    if (busy) next.add(itemId);
    else next.delete(itemId);
    uploading.value = next;
  }

  async function upload(itemId: string, file: File): Promise<boolean> {
    const sessionId = session.sessionId.value;
    if (!sessionId || uploading.value.has(itemId)) return false;
    // The leave guard waits for it, like any other save.
    const release = session.hold();
    mark(itemId, true);
    try {
      const path = await uploadMenuPhoto(file);
      // Another store's session by now: the photo belongs to nothing on screen.
      if (disposed || session.sessionId.value !== sessionId) return false;
      const saved = await session.mutate([
        { op: 'updateItem', itemId, photo: path },
      ]);
      return saved !== null;
    } catch (error) {
      console.error('[menu-import] dish photo upload failed', error);
      if (!disposed) ui.showToast(AlertType.Error, t(dishPhotoErrorKey(error)));
      return false;
    } finally {
      if (!disposed) mark(itemId, false);
      release();
    }
  }

  onScopeDispose(() => {
    disposed = true;
  });

  return { uploading, upload };
}
